import React from 'react';
import './App.css';
import LinkButton from './Components/LinkButton';
import EditorChrome from './Components/EditorChrome';
import {journalPapers, conferencePapers, githubProjects} from './Content/PortfolioContent';

class App extends React.Component
{
    render()
    {
        const focusAreas = [
            {
                title: 'BIRDRONE',
                meta: 'Sensing AI / Autonomous Drones',
                text: 'Sensor arrays, embedded intelligence, and autonomous drone robotics for orchard monitoring and bird deterrence.',
                link: 'https://birdrone.com.au'
            },
            {
                title: 'Contract Audio DSP & ML',
                meta: 'Ticking Heart / PlantSpeak',
                text: 'Applied audio, sensing, embedded, and ML systems for client products, including wearable medical audio and plant-sensing workflows.'
            },
            {
                title: 'PhD Research',
                meta: 'Biosignals / Cardiovascular ML',
                text: 'Machine learning, acoustic signal processing, and multichannel PCG/ECG analysis for cardiovascular disease detection.'
            },
            {
                title: 'Contract & Teaching',
                meta: 'Consulting / Sessional Academic',
                text: 'Software systems, report automation, full-stack tools, and tutoring across computing, DSP, embedded systems, and engineering.'
            }
        ];

        const workItems = [
            ['BIRDRONE', 'Co-Founder', '2025 - ongoing', 'Developing autonomous AI-assisted bird-deterrent technology using sensor arrays, embedded systems, and drone-based robotics for agricultural environments.'],
            ['PlantSpeak', 'Contract Engineering / AI Systems', '2025 - ongoing', 'Contract work across sensing, applied ML, and product-facing engineering for plant-focused signal and data workflows.'],
            ['Curtin University', 'Sessional Academic', '2022 - ongoing', 'Tutoring computing, software engineering, DSP, communications, electronics, electrical systems, and embedded systems engineering.'],
            ['Dolby Laboratories', 'Engineer / Intern Engineer', '2022 - 2023', 'Worked across Android audio QA, CI automation, embedded audio data collection, acoustic mapping analytics, and data pipelines using Python, Bash, Java, C/C++, React, and Databricks.'],
            ['FMG', 'Vacation Process Control Engineer', '2021 - 2022', 'Supported plant operations, PLC fault-finding, SCADA logic, and Train Loadout automation improvements.'],
            ['Cove Co.', 'Software Engineering Consultant', '2020 - 2022', 'Delivered report-automation systems, web applications, survey pipelines, and automated PDF generation tools.']
        ];

        const featuredJournalPapers = journalPapers
            .filter((paper) => paper.featuredOrder)
            .sort((a, b) => a.featuredOrder - b.featuredOrder);
        const featuredConferencePapers = conferencePapers.filter((paper) =>
            paper.title.startsWith('Noise-Robust') || paper.title.startsWith('Fine-Tuning of Wav2Vec')
        );

        const awards = [
            {
                title: 'GEn-CITy Best Paper Award 2026',
                text: 'Best Paper in Track 6: Intelligent Systems and Sensors for waveform-domain acoustic style transfer for underwater diver speech recognition.'
            },
            {
                title: 'IEEE Signal Processing Society Scholarship 2025',
                text: 'Recognition from the IEEE Signal Processing Society for high-achieving engineering students pursuing signal processing research and practice.'
            },
            {
                title: 'Western Power Prize 2023',
                text: 'Awarded for the second highest weighted average mark in the Electrical and Electronic Engineering course.'
            },
            {
                title: 'Nuheara Prize 2022',
                text: 'Awarded for the highest aggregate marks across Signals and Systems and Digital Signal Processing.'
            },
            {
                title: 'Vice-Chancellors List 2021 Semester 2',
                text: 'Recognition for achieving a semester average within the top 1 percent of students.'
            },
            {
                title: 'PCBeTheChange Winners 2021',
                text: 'International PCB design competition win for a hardware project centred on practical sensing and electronics design.'
            },
            {
                title: 'Curtin Letter of Commendation 2019-2023',
                text: 'Repeated academic commendation for maintaining semester averages above 80 throughout the double degree.'
            }
        ];

        return (
            <section id="app">
                <div className="container portfolio-shell">
                    <EditorChrome />
                    <section className="hero-section">
                        <div className="hero-copy">
                            <p className="eyebrow"><span className="syntax-comment">;;</span> Audio DSP & ML / Sensing AI / Biosignal ML</p>
                            <h1>Milan Marocchi</h1>
                            <p className="hero-text">
                                I develop audio DSP and ML systems alongside real-time embedded engineering,
                                sensing AI, autonomous drone robotics, and biosignal research. My current work leads with
                                BIRDRONE's agricultural sensing platform, alongside contract audio and sensing systems for
                                Ticking Heart and PlantSpeak, and multichannel PCG/ECG modelling for cardiovascular disease detection.
                            </p>
                            <div className="hero-actions">
                                <LinkButton name="About" route="/About"/>
                                <a className="secondary-link" href="https://github.com/MilanMarocchi" target="_blank" rel="noreferrer">GitHub</a>
                                <a className="secondary-link" href="https://birdrone.com.au" target="_blank" rel="noreferrer">BIRDRONE</a>
                                <a className="secondary-link" href="https://www.linkedin.com/in/milan-marocchi-3785b1215/" target="_blank" rel="noreferrer">LinkedIn</a>
                                <a className="secondary-link" href="https://scholar.google.com/scholar?q=Milan+Marocchi" target="_blank" rel="noreferrer">Google Scholar</a>
                                <a className="secondary-link" href="/PodBox">PodBox</a>
                                <a className="secondary-link" href="mailto:milan@marcellovalentino.com">Email me</a>
                            </div>
                            <p className="minibuffer-prompt"><span>M-x</span> build-things-that-listen-and-learn <i aria-hidden="true"></i></p>
                        </div>
                        <div className="hero-panel">
                            <img
                                className="portrait-image"
                                src="/Profile/headshot.JPG"
                                alt="Milan Marocchi"
                                onError={(event) => { event.currentTarget.style.display = 'none'; }}
                            />
                            <span>Current focus</span>
                            <strong>Audio DSP & ML, sensing AI for autonomous drones, and biosignal machine learning.</strong>
                        </div>
                    </section>

                    <section className="section-block github-section">
                        <div className="section-heading">
                            <h2>Recent GitHub Projects</h2>
                            <p>Research code, embedded audio tools, and signal-analysis platforms.</p>
                        </div>
                        <div className="github-grid">
                            {githubProjects.map((project) =>
                                <a className="github-card" href={project.url} target="_blank" rel="noreferrer" key={project.title}>
                                    <div className={'project-visual ' + project.visual}>
                                        <div className="visual-screen">
                                            <span></span>
                                            <span></span>
                                            <span></span>
                                            <strong>{project.label}</strong>
                                        </div>
                                    </div>
                                    <div className="github-card-body">
                                        <p>{project.updated}</p>
                                        <h3>{project.title}</h3>
                                        <span>{project.meta}</span>
                                        <small>{project.description}</small>
                                    </div>
                                </a>
                            )}
                        </div>
                    </section>

                    <section className="section-block">
                        <div className="section-heading">
                            <h2>Current Work</h2>
                            <p>BIRDRONE sensing AI first, then contract audio systems and biosignal research.</p>
                        </div>
                        <div className="focus-grid">
                            {focusAreas.map((item) =>
                                <article className="focus-card" key={item.title}>
                                    <div className="card-heading">
                                        <p>{item.meta}</p>
                                        <h3>{item.title}</h3>
                                    </div>
                                    <span>{item.text}</span>
                                    {item.link &&
                                        <a className="inline-card-link" href={item.link} target="_blank" rel="noreferrer">Visit BIRDRONE</a>
                                    }
                                </article>
                            )}
                        </div>
                    </section>

                    <section className="section-block">
                        <div className="section-heading">
                            <h2>Experience</h2>
                            <p>Autonomous drone sensing, contract audio and AI systems, research, and teaching.</p>
                        </div>
                        <div className="timeline">
                            {workItems.map((item) =>
                                <article className="timeline-item" key={item[0] + item[2]}>
                                    <div className="card-heading">
                                        <p>{item[2]}</p>
                                        <h3>{item[0]}</h3>
                                        <span>{item[1]}</span>
                                    </div>
                                    <p>{item[3]}</p>
                                </article>
                            )}
                        </div>
                    </section>

                    <section className="section-block awards-section">
                        <div className="section-heading">
                            <h2>Awards</h2>
                            <p>Selected recognition across signal processing, engineering, and academic achievement.</p>
                        </div>
                        <div className="award-list">
                            {awards.map((award) =>
                                <article className="award-pill" key={award.title}>
                                    <span>{award.title}</span>
                                    <small>{award.text}</small>
                                </article>
                            )}
                        </div>
                    </section>

                    <section className="section-block publication-section">
                        <div className="section-heading">
                            <h2>Featured Publications</h2>
                            <p>Selected Journal of Vascular Diseases, Biomedical Signal Processing and Control, ICASSP, and EMBC papers.</p>
                        </div>
                        <div className="publication-grid">
                            <div>
                                <h3>Journal Papers</h3>
                                {featuredJournalPapers.map((paper) =>
                                    <article className="publication-item" key={paper.title}>
                                        <div className="card-heading">
                                            <h4>{paper.title}</h4>
                                        </div>
                                        <p>{paper.detail}</p>
                                        <a href={paper.link} target="_blank" rel="noreferrer">{paper.link}</a>
                                    </article>
                                )}
                            </div>
                            <div>
                                <h3>Conference Papers</h3>
                                {featuredConferencePapers.map((paper) =>
                                    <article className="publication-item" key={paper.title}>
                                        <div className="card-heading">
                                            <h4>{paper.title}</h4>
                                        </div>
                                        <p>{paper.detail}</p>
                                        <a href={paper.link} target="_blank" rel="noreferrer">View paper ↗</a>
                                    </article>
                                )}
                            </div>
                        </div>
                        <div className="section-cta">
                            <LinkButton name="View All Publications" route="/Publications"/>
                        </div>
                    </section>

                </div>
            </section>
        );
    }
}

export default App;
